import React, { useCallback, useEffect, useState } from 'react';
import { appLeadsAPI, marketingAPI } from '../services/api';
import {
  LEAD_SOURCES, SOURCE_COLORS, DISMISS_REASONS, LEAD_STEPS, sourceLabel, paidValue, paidFromValue,
} from './shared/leadSources';
import {
  FiRefreshCw, FiPhone, FiMail, FiMapPin, FiCheck, FiX, FiUserPlus, FiActivity, FiClock,
} from 'react-icons/fi';
import './AppLeads.css';

const STATUS_TABS = [
  { key: 'NEW', label: 'New' },
  { key: 'CONTACTED', label: 'Contacted' },
  { key: 'CONVERTED', label: 'Converted' },
  { key: 'DISMISSED', label: 'Dismissed' },
  { key: '', label: 'All' },
];

const EVENT_LABEL = {
  APP_OPEN: 'Opened the app',
  VIEW_OPPORTUNITIES: 'Viewed Opportunities',
  OPEN_OPPORTUNITY: 'Opened an opportunity',
  SAVE_OPPORTUNITY: 'Saved an opportunity',
  UNSAVE_OPPORTUNITY: 'Removed a save',
  SEARCH: 'Searched',
  VIEW_GUIDES: 'Viewed Guides',
  OPEN_GUIDE: 'Opened a country guide',
  OPEN_LEAD_FORM: 'Opened a consultation form',
  QUIZ_START: 'Started the eligibility quiz',
  OTHER: 'Activity',
};

function timeAgo(iso) {
  if (!iso) return '';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// source='APP' (default): leads from the mobile app only.
// source='WEBSITE': Job/Tourism/Education consultation submissions from the
// website. The two are never mixed — same triage UI, filtered by origin.
export default function AppLeads({ source = 'APP' }) {
  const pageTitle = source === 'WEBSITE' ? 'Website Leads' : 'App Leads';
  const emptyLabel = source === 'WEBSITE' ? 'No website leads in this view.' : 'No leads in this view.';
  const [statusFilter, setStatusFilter] = useState('NEW');
  // CSM1: New leads not answered within one working day (Mon-Fri, Harare time).
  // ?overdue=1 (a link from My Analytics, KPI CSM1) opens the overdue list.
  const [overdueOnly, setOverdueOnly] = useState(() => new URLSearchParams(window.location.search).get('overdue') === '1');
  const [responseStats, setResponseStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // Marketing channel filter + the CMO's campaigns (empty when this login can't read the plan).
  const [sourceFilter, setSourceFilter] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [dismissing, setDismissing] = useState(false);
  const [dismissReason, setDismissReason] = useState('');

  useEffect(() => {
    marketingAPI.campaigns().then(({ data }) => setCampaigns(data.results || data || [])).catch(() => setCampaigns([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { source };
      if (statusFilter) params.status = statusFilter;
      if (overdueOnly) params.overdue = 1;
      if (sourceFilter) params.lead_source = sourceFilter;
      const { data } = await appLeadsAPI.list(params);
      setLeads(data.results || data);
    } catch (e) {
      setError('Could not load leads.');
    } finally {
      setLoading(false);
    }
    try {
      const { data } = await appLeadsAPI.responseStats(source);
      setResponseStats(data);
    } catch {
      setResponseStats(null);
    }
  }, [statusFilter, overdueOnly, source, sourceFilter]);

  useEffect(() => { load(); }, [load]);

  // Replace one lead in the list and the open panel after a tick or a source change.
  const refreshLead = (lead) => {
    setSelected(lead);
    setLeads((prev) => prev.map((l) => (l.id === lead.id ? lead : l)));
  };

  const saveSource = async (body) => {
    if (!selected) return;
    try {
      const { data } = await appLeadsAPI.setSource(selected.id, body);
      refreshLead(data);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not save the source.');
    }
  };

  const toggleStep = async (step, done) => {
    if (!selected) return;
    try {
      const { data } = await appLeadsAPI.step(selected.id, step, done);
      refreshLead(data);
      appLeadsAPI.responseStats(source).then((r) => setResponseStats(r.data)).catch(() => {});
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not save the tick.');
    }
  };

  const openLead = async (lead) => {
    setSelected(lead);
    setDismissing(false);
    setDismissReason('');
    setActivity([]);
    setActivityLoading(true);
    try {
      const { data } = await appLeadsAPI.activity(lead.id);
      setActivity(data || []);
    } catch {
      setActivity([]);
    } finally {
      setActivityLoading(false);
    }
  };

  const act = async (fn) => {
    if (!selected) return;
    setBusy(true);
    try {
      await fn(selected.id);
      await load();
      setSelected(null);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Action failed.');
    } finally {
      setBusy(false);
    }
  };

  const convertWith = async (apiFn, targetLabel) => {
    if (!selected) return;
    let email = selected.email;
    if (!email) {
      email = window.prompt(`An email is required to create a ${targetLabel}. Enter one:`);
      if (!email) return;
    }
    const body = { email };
    // Leads often give a single name ("Precious"); every client record needs
    // a surname, so ask for it here rather than letting the save fail.
    const name = (selected.name || '').trim();
    if (!name.includes(' ')) {
      const lastName = window.prompt(
        `"${name}" only gave one name. Enter their surname to create the ${targetLabel}:`,
      );
      if (!lastName || !lastName.trim()) return;
      body.first_name = name;
      body.last_name = lastName.trim();
    }
    await act((id) => apiFn(id, body));
  };

  const convert = () => convertWith(appLeadsAPI.convert, 'student');
  const convertToJob = () => convertWith(appLeadsAPI.convertToJob, 'job-seeker client');
  const convertToTourist = () => convertWith(appLeadsAPI.convertToTourist, 'tourist client');

  return (
    <div className="appleads">
      <div className="appleads-header">
        <h1>{pageTitle}</h1>
        <button className="appleads-refresh" onClick={load} disabled={loading}>
          <FiRefreshCw className={loading ? 'spin' : ''} /> Refresh
        </button>
      </div>

      {responseStats && (
        <div className="appleads-clock">
          <span className={responseStats.overdue_count > 0 ? 'appleads-overdue-count' : 'appleads-muted'}>
            <FiClock /> {responseStats.overdue_count} overdue
          </span>
          {responseStats.month && (
            <span
              className="appleads-muted"
              title="Leads this month answered within one working day ÷ leads whose deadline has passed or that were answered (CSM1)."
            >
              First response this month:{' '}
              <strong>{responseStats.month.pct != null ? `${responseStats.month.pct}%` : '—'}</strong>
              {' '}({responseStats.month.on_time}/{responseStats.month.on_time + responseStats.month.late} within 1 working day
              {responseStats.month.pending > 0 ? `, ${responseStats.month.pending} still in window` : ''})
              {' · '}target {responseStats.month.target_pct}%
              {responseStats.month_all_sources?.pct != null && ` · all leads ${responseStats.month_all_sources.pct}%`}
            </span>
          )}
        </div>
      )}

      <div className="appleads-source-filter">
        <label>
          Source{' '}
          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
            <option value="">All sources</option>
            <option value="NONE">Not recorded</option>
            {LEAD_SOURCES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
      </div>

      <div className="appleads-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            className={statusFilter === t.key && !overdueOnly ? 'active' : ''}
            onClick={() => { setOverdueOnly(false); setStatusFilter(t.key); }}
          >
            {t.label}
          </button>
        ))}
        <button
          className={overdueOnly ? 'active' : ''}
          onClick={() => { setStatusFilter('NEW'); setOverdueOnly(true); }}
          title="New leads not answered within one working day (Mon-Fri, CAT)"
        >
          Overdue{responseStats ? ` (${responseStats.overdue_count})` : ''}
        </button>
      </div>

      {error && <div className="appleads-error">{error}</div>}

      <div className="appleads-body">
        <div className="appleads-list">
          {loading && <div className="appleads-empty">Loading…</div>}
          {!loading && leads.length === 0 && (
            <div className="appleads-empty">{emptyLabel}</div>
          )}
          {leads.map((l) => (
            <button
              key={l.id}
              className={`appleads-row ${selected?.id === l.id ? 'selected' : ''}`}
              onClick={() => openLead(l)}
            >
              <div className="appleads-row-top">
                <span className="appleads-name">{l.name}</span>
                {l.student_code && <span className="appleads-existing">Existing student</span>}
                {l.is_overdue && <span className="appleads-overdue" title="Not answered within one working day"><FiClock /> Overdue</span>}
                <span className={`appleads-chan chan-${l.channel}`}>{l.channel_display}</span>
                {l.lead_source ? (
                  <span className="appleads-source" style={{ borderColor: SOURCE_COLORS[l.lead_source] || '#cbd5e1', color: SOURCE_COLORS[l.lead_source] || '#475569' }}>
                    {sourceLabel(l.lead_source)}{l.lead_paid ? ' · paid' : ''}
                  </span>
                ) : (
                  <span className="appleads-source missing" title="No source recorded (KPI CMO3)">No source</span>
                )}
              </div>
              <div className="appleads-row-sub">
                <span><FiPhone /> {l.phone}</span>
                {l.destination_country && <span><FiMapPin /> {l.destination_country}</span>}
                <span>{l.intent_display}</span>
              </div>
              <div className="appleads-row-meta">
                <span>{timeAgo(l.created_at)}</span>
                {l.activity_count > 0 && (
                  <span className="appleads-signal"><FiActivity /> {l.activity_count}</span>
                )}
              </div>
            </button>
          ))}
        </div>

        <div className="appleads-detail">
          {!selected && <div className="appleads-empty">Select a lead.</div>}
          {selected && (
            <>
              <h2>{selected.name}</h2>
              {selected.student_code && (
                <div className="appleads-existing-note">
                  Sent while signed in to the app by existing student{' '}
                  <strong>{selected.student_name} ({selected.student_code})</strong>.
                </div>
              )}
              <div className="appleads-facts">
                <div><FiPhone /> {selected.phone}</div>
                {selected.email && <div><FiMail /> {selected.email}</div>}
                <div>{selected.channel_display} · {selected.intent_display}</div>
                {selected.destination_country && (
                  <div><FiMapPin /> {selected.destination_country}</div>
                )}
                <div className="appleads-muted">{timeAgo(selected.created_at)} · status {selected.status_display}</div>
              </div>

              <div className="appleads-box">
                <strong>Where they came from</strong>
                <div className="appleads-source-form">
                  <select value={selected.lead_source || ''} onChange={(e) => saveSource({ lead_source: e.target.value })}>
                    <option value="">Not recorded</option>
                    {LEAD_SOURCES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <select value={paidValue(selected.lead_paid)} onChange={(e) => saveSource({ lead_paid: paidFromValue(e.target.value) })}>
                    <option value="">Paid or organic?</option>
                    <option value="PAID">Paid ad</option>
                    <option value="ORGANIC">Organic</option>
                  </select>
                  {campaigns.length > 0 && (
                    <select value={selected.campaign || ''} onChange={(e) => saveSource({ campaign: e.target.value || null })}>
                      <option value="">No campaign</option>
                      {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  )}
                </div>
                {(selected.how_heard || (selected.utm && Object.keys(selected.utm).length > 0)) && (
                  <div className="appleads-muted" style={{ marginTop: 6 }}>
                    {selected.how_heard && <>They said: “{selected.how_heard}”. </>}
                    {selected.utm && Object.keys(selected.utm).length > 0 && (
                      <>Link tags: {Object.entries(selected.utm).map(([k, v]) => `${k.replace('utm_', '')}=${v}`).join(', ')}</>
                    )}
                  </div>
                )}
              </div>

              <div className="appleads-box">
                <strong>Follow-up</strong>
                <div className="appleads-steps">
                  {LEAD_STEPS.map((st) => {
                    const tick = selected.steps?.[st.value];
                    return (
                      <label key={st.value} className={tick ? 'done' : ''}>
                        <input type="checkbox" checked={!!tick} onChange={(e) => toggleStep(st.value, e.target.checked)} />
                        {st.label}
                        {tick && <span className="appleads-muted"> · {tick.by || ''} {timeAgo(tick.done_at)}</span>}
                      </label>
                    );
                  })}
                </div>
              </div>

              {selected.message && (
                <div className="appleads-message">“{selected.message}”</div>
              )}

              {selected.quiz_answers && Object.keys(selected.quiz_answers).length > 0 && (
                <div className="appleads-quiz">
                  <strong>Quiz answers</strong>
                  <pre>{JSON.stringify(selected.quiz_answers, null, 2)}</pre>
                </div>
              )}

              <div className="appleads-activity">
                <strong>Browsing before this lead</strong>
                {activityLoading && <div className="appleads-muted">Loading…</div>}
                {!activityLoading && activity.length === 0 && (
                  <div className="appleads-muted">No activity recorded.</div>
                )}
                <ul>
                  {activity.map((e) => (
                    <li key={e.id}>
                      <span>{EVENT_LABEL[e.event_type] || e.event_type}</span>
                      {e.ref && <code>{e.ref}</code>}
                      <span className="appleads-muted">{timeAgo(e.created_at)}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {selected.status !== 'CONVERTED' && selected.status !== 'DISMISSED' && (
                <div className="appleads-actions">
                  <button disabled={busy} onClick={() => act(appLeadsAPI.markContacted)}>
                    <FiCheck /> Mark contacted
                  </button>
                  {/* Convert button matches the lead's declared intent — a Job
                      or Tourist lead can never become a Student. When the
                      intent isn't known yet (UNSURE), all three are offered
                      so the consultant picks after speaking to the person. */}
                  {/* An existing student can't be converted to a student again. */}
                  {!selected.student_code && (selected.intent === 'STUDY' || selected.intent === 'UNSURE') && (
                    <button disabled={busy} className="primary" onClick={convert}>
                      <FiUserPlus /> Convert to student
                    </button>
                  )}
                  {(selected.intent === 'WORK' || selected.intent === 'UNSURE') && (
                    <button disabled={busy} className="primary" onClick={convertToJob}>
                      <FiUserPlus /> Convert to job
                    </button>
                  )}
                  {(selected.intent === 'TRAVEL' || selected.intent === 'UNSURE') && (
                    <button disabled={busy} className="primary" onClick={convertToTourist}>
                      <FiUserPlus /> Convert to tourist
                    </button>
                  )}
                  <button disabled={busy} className="danger" onClick={() => setDismissing(true)}>
                    <FiX /> Dismiss
                  </button>
                </div>
              )}
              {dismissing && selected.status !== 'DISMISSED' && (
                <div className="appleads-dismiss">
                  <select value={dismissReason} onChange={(e) => setDismissReason(e.target.value)}>
                    <option value="">Why dismiss this lead?</option>
                    {DISMISS_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                  <button disabled={busy || !dismissReason} className="danger" onClick={() => act((id) => appLeadsAPI.dismiss(id, dismissReason))}>
                    Dismiss
                  </button>
                  <button disabled={busy} onClick={() => setDismissing(false)}>Cancel</button>
                </div>
              )}
              {selected.status === 'DISMISSED' && selected.dismiss_reason_display && (
                <div className="appleads-muted">Dismissed: {selected.dismiss_reason_display}</div>
              )}
              {selected.status === 'CONVERTED' && selected.converted_student_id && (
                <div className="appleads-muted">Converted → {selected.converted_student_id}</div>
              )}
              {selected.status === 'CONVERTED' && selected.converted_job_id && (
                <div className="appleads-muted">Converted → {selected.converted_job_id}</div>
              )}
              {selected.status === 'CONVERTED' && selected.converted_tourist_id && (
                <div className="appleads-muted">Converted → {selected.converted_tourist_id}</div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
